import { Map } from '@/lib/types/Map';
import getBoundsFromMap from './getBoundsFromMap';

/**
 * Interface for coordinate conversion results
 */
export interface CoordinateConversionResult {
    x: number;
    y: number;
}

/**
 * Interface for Leaflet coordinates
 */
export interface LeafletCoordinate {
    lat: number;
    lng: number;
}

/**
 * Calculate map dimensions and scales for coordinate conversion
 */
function getMapConversionData(map: Map) {
    if (!map.size) {
        throw new Error('Map size is required for coordinate conversion');
    }

    const bounds = getBoundsFromMap(map) as [[number, number], [number, number]];
    
    const leafletMapXSize = bounds[1][1] - bounds[0][1];
    const leafletMapYSize = bounds[1][0] - bounds[0][0];
    const eorMapXSize = map.size.x - 1;
    const eorMapYSize = map.size.y - 1;
    
    const scaleX = eorMapXSize / leafletMapXSize;
    const scaleY = eorMapYSize / leafletMapYSize;

    return {
        bounds,
        leafletMapXSize,
        leafletMapYSize,
        eorMapXSize,
        eorMapYSize,
        scaleX,
        scaleY
    };
}

/**
 * Convert Leaflet coordinates to game coordinates (real coordinates)
 */
export function leafletToGameCoordinates(
    leafletLat: number, 
    leafletLng: number, 
    map: Map
): CoordinateConversionResult {
    const { leafletMapXSize, leafletMapYSize, scaleX, scaleY } = getMapConversionData(map);
    
    const realX = (leafletLng + leafletMapXSize / 2) * scaleX + 1;
    const realY = (-leafletLat + leafletMapYSize / 2) * scaleY + 1;
    
    return { x: realX, y: realY };
}

/**
 * Convert game coordinates (real coordinates) to Leaflet coordinates
 */
export function gameToLeafletCoordinates(
    gameX: number, 
    gameY: number, 
    map: Map
): LeafletCoordinate {
    const { leafletMapXSize, leafletMapYSize, scaleX, scaleY } = getMapConversionData(map);
    
    const leafletLng = (gameX - 1) / scaleX - leafletMapXSize / 2;
    const leafletLat = -((gameY - 1) / scaleY - leafletMapYSize / 2);
    
    return { lat: leafletLat, lng: leafletLng };
}

/**
 * Check if Leaflet coordinates are within map bounds
 */
export function isWithinMapBounds(
    leafletLat: number, 
    leafletLng: number, 
    map: Map
): boolean {
    const { bounds } = getMapConversionData(map);
    
    return leafletLat >= bounds[0][0] && leafletLat <= bounds[1][0] &&
           leafletLng >= bounds[0][1] && leafletLng <= bounds[1][1];
}

/**
 * Calculate the maximum possible distance within a map
 * Returns the diagonal distance from corner to corner
 */
export function calculateMaxMapDistance(map: Map): number {
    if (!map.size) {
        throw new Error('Map size is required for distance calculation');
    }
    
    // Calculate diagonal distance from (1,1) to (maxX, maxY)
    const maxX = map.size.x;
    const maxY = map.size.y;
    
    return Math.sqrt(Math.pow(maxX - 1, 2) + Math.pow(maxY - 1, 2));
}

/**
 * Calculate distance between two game coordinates
 */
export function calculateGameCoordinateDistance(
    coord1: { x: number; y: number },
    coord2: { x: number; y: number }
): number {
    return Math.sqrt(
        Math.pow(coord1.x - coord2.x, 2) + 
        Math.pow(coord1.y - coord2.y, 2)
    );
}

/**
 * Convert game coordinate distance to yalms (1 unit = 44.44 yalms)
 * Rounds to nearest integer with no decimals
 */
export function convertDistanceToYalms(distanceInUnits: number): number {
    return Math.round(distanceInUnits * 44.44);
}